'use client';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Drawer,
  DrawerContent,
  DrawerDescription,
  DrawerFooter,
  DrawerHeader,
  DrawerTitle,
} from '@/components/ui/drawer';
import * as React from 'react';
import { useTranslations } from 'next-intl';

type AlertInput =
  | string
  | {
      title?: string;
      description: React.ReactNode;
      okText?: string;
    };

type AlertRequest = {
  title?: string;
  description: React.ReactNode;
  okText: string;
  resolve: () => void;
};

type ConfirmInputObject = {
  title?: string;
  description: React.ReactNode;
  /**
   * Optional rich body content rendered below the description.
   * Use this for inputs, lists, or any non-text content to avoid nesting
   * block elements inside the description paragraph.
   */
  body?: React.ReactNode;
  confirmText?: string;
  cancelText?: string;
  destructive?: boolean;
  initialFocus?: 'confirm' | 'cancel';
};

type ConfirmRequest = {
  title?: string;
  description: React.ReactNode;
  confirmText: string;
  cancelText: string;
  resolve: (value: boolean) => void;
  body?: React.ReactNode;
  destructive?: boolean;
  initialFocus?: 'confirm' | 'cancel';
};

type AlertDialogContextValue = {
  alert: (input: AlertInput) => Promise<void>;
  confirm: (input: string | ConfirmInputObject) => Promise<boolean>;
};

const AlertDialogContext = React.createContext<
  AlertDialogContextValue | undefined
>(undefined);

export function AlertDialogProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const t = useTranslations('Common');
  const [queue, setQueue] = React.useState<Array<AlertRequest>>([]);
  const current = queue.length > 0 ? queue[0] : null;
  const [open, setOpen] = React.useState(false);
  const [isMobile, setIsMobile] = React.useState(false);
  const [confirmState, setConfirmState] = React.useState<ConfirmRequest | null>(
    null
  );

  React.useEffect(() => {
    setOpen(Boolean(current));
  }, [current]);

  React.useEffect(() => {
    const check = () =>
      setIsMobile(window.matchMedia('(max-width: 640px)').matches);
    check();
    window.addEventListener('resize', check);
    return () => window.removeEventListener('resize', check);
  }, []);

  const closeCurrent = React.useCallback(() => {
    setOpen(false);
    if (current) {
      // Resolve the awaiting promise, then dequeue
      current.resolve();
      setQueue((q) => q.slice(1));
    }
  }, [current]);

  const alert = React.useCallback(
    (input: AlertInput) => {
      return new Promise<void>((resolve) => {
        let req: AlertRequest;
        if (typeof input === 'string') {
          req = { description: input, okText: t('ok'), resolve };
        } else {
          const base = {
            description: input.description,
            okText: input.okText ?? t('ok'),
            resolve,
          } as const;
          req = input.title ? { title: input.title, ...base } : base;
        }
        setQueue((q) => [...q, req]);
      });
    },
    [t]
  );

  const confirm = React.useCallback(
    (input: string | ConfirmInputObject) => {
      return new Promise<boolean>((resolve) => {
        const state: ConfirmRequest =
          typeof input === 'string'
            ? {
                description: input,
                confirmText: t('confirm'),
                cancelText: t('cancel'),
                destructive: false,
                initialFocus: 'cancel',
                resolve,
              }
            : {
                ...(input.title ? { title: input.title } : {}),
                description: input.description,
                confirmText: input.confirmText ?? t('confirm'),
                cancelText: input.cancelText ?? t('cancel'),
                destructive: input.destructive ?? false,
                initialFocus: input.initialFocus ?? 'cancel',
                resolve,
                ...(input.body ? { body: input.body } : {}),
              };
        setConfirmState(state);
      });
    },
    [t]
  );

  const cancelButtonRef = React.useRef<HTMLButtonElement>(null);
  const confirmButtonRef = React.useRef<HTMLButtonElement>(null);
  const focusInitialConfirmAction = React.useCallback(
    (event: Event) => {
      if (!confirmState) return;
      event.preventDefault();
      const target =
        confirmState.initialFocus === 'confirm'
          ? confirmButtonRef.current
          : cancelButtonRef.current;
      target?.focus({ preventScroll: true });
    },
    [confirmState]
  );

  const value = React.useMemo<AlertDialogContextValue>(
    () => ({ alert, confirm }),
    [alert, confirm]
  );

  return (
    <AlertDialogContext.Provider value={value}>
      {children}
      {isMobile ? (
        <Drawer
          open={open || Boolean(confirmState)}
          onOpenChange={(o) => {
            if (o) return;
            if (confirmState) {
              confirmState.resolve(false);
              setConfirmState(null);
            } else {
              closeCurrent();
            }
          }}
        >
          {confirmState ? (
            <DrawerContent onOpenAutoFocus={focusInitialConfirmAction}>
              <DrawerHeader>
                <DrawerTitle
                  className={confirmState.title ? undefined : 'sr-only'}
                >
                  {confirmState.title || t('confirm')}
                </DrawerTitle>
                <DrawerDescription className="whitespace-pre-wrap">
                  {confirmState.description}
                </DrawerDescription>
              </DrawerHeader>
              {confirmState.body ? (
                <div className="px-4 pt-1 pb-4 sm:px-6">
                  {confirmState.body}
                </div>
              ) : null}
              <DrawerFooter>
                <Button
                  ref={confirmButtonRef}
                  autoFocus={confirmState.initialFocus === 'confirm'}
                  variant={confirmState.destructive ? 'destructive' : 'default'}
                  onClick={() => {
                    confirmState.resolve(true);
                    setConfirmState(null);
                  }}
                >
                  {confirmState.confirmText}
                </Button>
                <Button
                  ref={cancelButtonRef}
                  autoFocus={confirmState.initialFocus === 'cancel'}
                  variant="outline"
                  onClick={() => {
                    confirmState.resolve(false);
                    setConfirmState(null);
                  }}
                >
                  {confirmState.cancelText}
                </Button>
              </DrawerFooter>
            </DrawerContent>
          ) : current ? (
            <DrawerContent>
              <DrawerHeader>
                <DrawerTitle className={current.title ? undefined : 'sr-only'}>
                  {current.title || t('alert')}
                </DrawerTitle>
                <DrawerDescription className="whitespace-pre-wrap">
                  {current.description}
                </DrawerDescription>
              </DrawerHeader>
              <DrawerFooter>
                <Button autoFocus onClick={closeCurrent}>
                  {current.okText}
                </Button>
              </DrawerFooter>
            </DrawerContent>
          ) : null}
        </Drawer>
      ) : (
        <Dialog
          open={open || Boolean(confirmState)}
          onOpenChange={(o) => {
            if (o) return;
            if (confirmState) {
              confirmState.resolve(false);
              setConfirmState(null);
            } else {
              closeCurrent();
            }
          }}
        >
          {confirmState ? (
            <DialogContent
              showCloseButton={false}
              className="gap-2 rounded-2xl p-2 sm:max-w-md"
              onOpenAutoFocus={focusInitialConfirmAction}
            >
              <DialogHeader>
                <DialogTitle
                  className={confirmState.title ? 'sr-only' : 'sr-only'}
                >
                  {confirmState.title || t('confirm')}
                </DialogTitle>
                <DialogDescription className="text-muted-foreground px-2 pt-1 text-sm whitespace-pre-wrap">
                  {confirmState.description}
                </DialogDescription>
              </DialogHeader>
              {confirmState.body ? (
                <div className="px-2">{confirmState.body}</div>
              ) : null}
              <div className="flex w-full items-center justify-between gap-2">
                <Button
                  ref={cancelButtonRef}
                  autoFocus={confirmState.initialFocus === 'cancel'}
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    confirmState.resolve(false);
                    setConfirmState(null);
                  }}
                >
                  {confirmState.cancelText}
                </Button>
                <Button
                  ref={confirmButtonRef}
                  autoFocus={confirmState.initialFocus === 'confirm'}
                  size="sm"
                  variant={confirmState.destructive ? 'destructive' : 'default'}
                  onClick={() => {
                    confirmState.resolve(true);
                    setConfirmState(null);
                  }}
                >
                  {confirmState.confirmText}
                </Button>
              </div>
            </DialogContent>
          ) : current ? (
            <DialogContent>
              <DialogHeader>
                <DialogTitle className={current.title ? undefined : 'sr-only'}>
                  {current.title || t('alert')}
                </DialogTitle>
                <DialogDescription className="whitespace-pre-wrap">
                  {current.description}
                </DialogDescription>
              </DialogHeader>
              <DialogFooter>
                <Button autoFocus onClick={closeCurrent}>
                  {current.okText}
                </Button>
              </DialogFooter>
            </DialogContent>
          ) : null}
        </Dialog>
      )}
    </AlertDialogContext.Provider>
  );
}

export function useAlertDialog(): AlertDialogContextValue {
  const ctx = React.useContext(AlertDialogContext);
  if (!ctx) {
    throw new Error('useAlertDialog must be used within AlertDialogProvider');
  }
  return ctx;
}
